// src/components/lds/LdsOverview.tsx
import type {
  ComponentType,
  CSSProperties,
  Key,
  ReactNode,
} from "react";

export type LdsOverviewIcon = ComponentType<{
  className?: string;
  "aria-hidden"?: boolean;
}>;

type LdsOverviewPageProps = {
  children: ReactNode;
  className?: string;
  styles?: ReactNode;
};

type LdsOverviewHeroProps = {
  icon: LdsOverviewIcon;
  eyebrow: string;
  title: string;
  description?: string;
  actions?: ReactNode;
  className?: string;
};

type LdsOverviewSectionProps = {
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
};

type LdsOverviewGridProps = {
  children: ReactNode;
  className?: string;
};

type LdsOverviewCardProps = {
  icon: LdsOverviewIcon;
  eyebrow: string;
  title: string;
  description: string;
  actionLabel?: string;
  onClick: () => void;
  animationIndex?: number;
  className?: string;
  key?: Key;
};

type LdsOverviewEmptyProps = {
  children: ReactNode;
  className?: string;
};

const overviewStyles = `
  @keyframes ldsOverviewPageEnter {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes ldsOverviewCardEnter {
    from {
      opacity: 0;
      transform: translateY(18px) scale(0.985);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .lds-overview-page-enter {
    animation: ldsOverviewPageEnter 420ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .lds-overview-card-enter {
    animation: ldsOverviewCardEnter 520ms cubic-bezier(0.22, 1, 0.36, 1);
    animation-fill-mode: both;
  }

  .lds-overview-stagger > :not(.lds-overview-card-enter) {
    animation: ldsOverviewCardEnter 520ms cubic-bezier(0.22, 1, 0.36, 1);
    animation-fill-mode: both;
  }

  .lds-overview-stagger > :nth-child(1) { animation-delay: 60ms; }
  .lds-overview-stagger > :nth-child(2) { animation-delay: 120ms; }
  .lds-overview-stagger > :nth-child(3) { animation-delay: 180ms; }
  .lds-overview-stagger > :nth-child(4) { animation-delay: 240ms; }
  .lds-overview-stagger > :nth-child(5) { animation-delay: 300ms; }
  .lds-overview-stagger > :nth-child(6) { animation-delay: 360ms; }
  .lds-overview-stagger > :nth-child(7) { animation-delay: 420ms; }
  .lds-overview-stagger > :nth-child(8) { animation-delay: 480ms; }
  .lds-overview-stagger > :nth-child(n + 9) { animation-delay: 540ms; }

  @media (prefers-reduced-motion: reduce) {
    .lds-overview-page-enter,
    .lds-overview-card-enter,
    .lds-overview-stagger > * {
      animation: none !important;
    }
  }
`;

const cx = (
  ...values: Array<string | false | null | undefined>
) => values.filter(Boolean).join(" ");

export const LdsOverviewPage = ({
  children,
  className,
  styles,
}: LdsOverviewPageProps) => (
  <>
    <style>{overviewStyles}</style>
    {styles}

    <main
      className={cx(
        "lds-overview-page-enter px-4 py-5 sm:px-6 lg:px-8",
        className
      )}
    >
      <div className="mx-auto max-w-[1600px]">
        <div className="rounded-[32px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_26px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/85 sm:p-6">
          <div className="space-y-6">
            {children}
          </div>
        </div>
      </div>
    </main>
  </>
);

export const LdsOverviewHero = ({
  icon: Icon,
  eyebrow,
  title,
  description,
  actions,
  className,
}: LdsOverviewHeroProps) => (
  <section
    className={cx(
      "relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7",
      className
    )}
  >
    <Icon
      aria-hidden="true"
      className="pointer-events-none absolute right-10 top-1/2 hidden -translate-y-1/2 text-[8rem] text-indigo-200/55 dark:text-indigo-400/10 sm:block"
    />

    <div className="relative z-10 flex min-w-0 flex-col gap-5 lg:flex-row lg:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-5">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-violet-200 to-sky-50 text-3xl text-violet-700 shadow-lg dark:border-slate-900 dark:from-violet-800 dark:to-sky-950 dark:text-violet-100 sm:h-24 sm:w-24">
          <Icon aria-hidden="true" />
        </div>

        <div className="min-w-0 max-w-3xl">
          <p className="text-sm font-bold uppercase tracking-[0.14em] text-indigo-700 dark:text-indigo-300">
            {eyebrow}
          </p>

          <h1 className="mt-2 text-4xl font-bold text-slate-950 dark:text-white">
            {title}
          </h1>

          {description ? (
            <p className="mt-4 text-base leading-7 text-slate-600 dark:text-slate-300">
              {description}
            </p>
          ) : null}
        </div>
      </div>

      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-3 lg:mr-36">{actions}</div> : null}
    </div>
  </section>
);

export const LdsOverviewSection = ({
  title,
  description,
  children,
  className,
}: LdsOverviewSectionProps) => (
  <section className={className}>
    <div className="mb-5">
      <h2 className="text-xl font-bold text-slate-950 dark:text-white">
        {title}
      </h2>

      {description ? (
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          {description}
        </p>
      ) : null}
    </div>

    {children}
  </section>
);

export const LdsOverviewGrid = ({
  children,
  className,
}: LdsOverviewGridProps) => (
  <div
    className={cx(
      "lds-overview-stagger grid grid-cols-1 gap-6 lg:grid-cols-2",
      className
    )}
  >
    {children}
  </div>
);

export const LdsOverviewCard = ({
  icon: Icon,
  eyebrow,
  title,
  description,
  actionLabel = "Open",
  onClick,
  animationIndex = 0,
  className,
}: LdsOverviewCardProps) => {
  const animationStyle = {
    animationDelay: `${60 + animationIndex * 60}ms`,
  } satisfies CSSProperties;

  return (
    <button
      type="button"
      onClick={onClick}
      style={animationStyle}
      className={cx(
        "lds-overview-card-enter group relative min-h-[230px] overflow-hidden rounded-[28px] border border-slate-200/80 bg-white p-6 text-left shadow-[0_14px_35px_rgba(15,23,42,0.07)] transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-1 hover:border-indigo-200 hover:shadow-[0_24px_55px_rgba(15,23,42,0.12)] focus:outline-none focus:ring-4 focus:ring-indigo-100 active:translate-y-0 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-indigo-800 dark:hover:shadow-[0_24px_55px_rgba(0,0,0,0.45)] dark:focus:ring-indigo-950 motion-reduce:transform-none motion-reduce:transition-none",
        className
      )}
    >
      <span className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-indigo-500 via-violet-500 to-sky-400 transition-transform duration-300 ease-out group-hover:scale-x-100 group-focus-visible:scale-x-100" />

      <div className="relative z-10 flex h-full flex-col">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-indigo-600 dark:text-indigo-300">
              {eyebrow}
            </p>

            <h2 className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
              {title}
            </h2>
          </div>

          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition-[transform,border-color,color,background-color] duration-200 ease-out group-hover:border-indigo-200 group-hover:bg-indigo-50 group-hover:text-indigo-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:group-hover:border-indigo-700 dark:group-hover:bg-indigo-950/50 dark:group-hover:text-indigo-300 motion-reduce:transition-none">
            <span
              aria-hidden="true"
              className="text-lg transition-transform duration-200 ease-out group-hover:translate-x-0.5 motion-reduce:transform-none motion-reduce:transition-none"
            >
              →
            </span>
          </span>
        </div>

        <p className="mt-4 max-w-xl text-sm leading-6 text-slate-600 dark:text-slate-300">
          {description}
        </p>

        <span className="mt-auto pt-6 text-sm font-bold text-indigo-700 dark:text-indigo-300">
          {actionLabel}
        </span>
      </div>

      <Icon
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-5 -right-4 text-[7.25rem] text-indigo-100 transition-[transform,color] duration-300 ease-out group-hover:translate-x-1 group-hover:translate-y-1 group-hover:scale-105 group-hover:text-indigo-200 dark:text-indigo-950/70 dark:group-hover:text-indigo-900/80 motion-reduce:transform-none motion-reduce:transition-none"
      />
    </button>
  );
};

export const LdsOverviewEmpty = ({
  children,
  className,
}: LdsOverviewEmptyProps) => (
  <section
    className={cx(
      "lds-overview-card-enter rounded-[28px] border border-slate-200/80 bg-white px-6 py-10 text-center shadow-[0_14px_35px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-950",
      className
    )}
  >
    <p className="text-sm text-slate-600 dark:text-slate-300">
      {children}
    </p>
  </section>
);
